/**
 * OpenAnswersDialog — ★ Fase 59: missione 'open' (domande a risposta libera).
 *
 * L'utente risponde con parole sue a 4-5 domande sul suo metodo di lavoro.
 * Le risposte sono OBBLIGATORIE per completare il training (min 10 caratteri
 * ciascuna, validate anche server-side) e vengono lette dall'admin nel
 * pannello «Progressi del team».
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Loader2, MessageSquareText } from 'lucide-react';

const MIN_LEN = 10;

export function OpenAnswersDialog({ mission, open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && mission) {
      const qs = mission.questions || [];
      const saved = mission.answers || [];
      setAnswers(qs.map((_, i) => saved[i] || ''));
    }
  }, [open, mission]);

  if (!mission) return null;
  const questions = mission.questions || [];
  const allValid = answers.length === questions.length
    && answers.every((a) => (a || '').trim().length >= MIN_LEN);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/training/open', {
        mission_key: mission.key,
        answers: answers.map((a) => (a || '').trim()),
      });
      toast.success(t('trainingPage.answersSent', { defaultValue: 'Risposte inviate! +{{points}} punti', points: data.points }), {
        description: data.finished
          ? t('trainingPage.trainingDone', 'Training completato: Certificato HCF ottenuto!')
          : t('trainingPage.thanksTeam', 'Grazie: il team HCF leggerà le tue risposte.'),
      });
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('trainingPage.answersSendError', 'Errore invio risposte'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="training-open-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareText className="w-5 h-5 text-[hsl(var(--primary))]" /> {mission.title}
          </DialogTitle>
          <DialogDescription>{mission.desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {questions.map((q, qi) => {
            const len = (answers[qi] || '').trim().length;
            const ok = len >= MIN_LEN;
            return (
              <div key={q} className="space-y-1.5">
                <Label htmlFor={`open-q-${qi}`} className="text-sm font-semibold leading-snug">
                  {qi + 1}. {q}
                </Label>
                <Textarea
                  id={`open-q-${qi}`}
                  rows={3}
                  value={answers[qi] || ''}
                  onChange={(e) => setAnswers((arr) => {
                    const next = [...arr];
                    next[qi] = e.target.value;
                    return next;
                  })}
                  placeholder={t('trainingPage.openPlaceholder', 'Scrivi la tua risposta con parole tue…')}
                  data-testid={`training-open-answer-${qi}`}
                  className={!ok && len > 0 ? 'border-[#E4C88A]' : ''}
                />
                <div className={`text-[11px] ${ok ? 'text-muted-foreground' : 'text-[#B7791F]'}`}>
                  {ok
                    ? t('trainingPage.chars', { defaultValue: '{{len}} caratteri', len })
                    : t('trainingPage.minChars', { defaultValue: 'Minimo {{min}} caratteri ({{len}}/{{min}})', min: MIN_LEN, len })}
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="training-open-cancel">{t('trainingPage.close', 'Chiudi')}</Button>
          <Button onClick={submit} disabled={!allValid || submitting} data-testid="training-open-submit">
            {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> {t('trainingPage.sending', 'Invio…')}</> : t('trainingPage.submitAnswers', 'Invia risposte')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
